import useTranslate from "@hooks/use_translate"
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import {
  promptLayerLabel,
  promptRowKey,
  type PromptLayer
} from "@pages/main/scripts/prompts/prompt.models"
import PromptDiffPanel from "@pages/main/scripts/prompts/sub_components/PromptDiffPanel"
import { useEffect, useMemo, useState } from "react"

export interface PromptLike {
  name: string
  description: string
  usage: string
  engineVersion?: PromptLayer
  versions?: PromptVersion[]
}

/** A name can be several rows, so the pickers address a ROW: `name::role::layer`. */
const rowLabel = (prompt: PromptLike) =>
  `${prompt.name} · ${promptLayerLabel(prompt.engineVersion)}`

interface PromptVersion extends PromptLike {}

interface PromptDiffProps {
  open: boolean
  onClose: () => void
  prompts: PromptLike[]
  defaultA?: string
  title?: string
  maxWidth?: "md" | "lg" | "xl"
}

function PromptSelectDialog({
  open,
  title,
  prompts,
  onClose,
  q,
  setQ
}: {
  open: boolean
  title: string
  prompts: PromptLike[]
  onClose: (picked?: string) => void
  q: string
  setQ: (q: string) => void
}) {
  const usages = Array.from(new Set(prompts.map((p) => p.usage || "UNDEFINED")))
  const match = (s: string) => s.toLowerCase().includes(q.toLowerCase())

  return (
    <Dialog open={open} onClose={() => onClose()} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers sx={{ pt: 1 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Ara…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Stack spacing={2}>
          {usages.map((u, idx) => {
            const items = prompts
              .filter((p) => (q ? match(p.name) || match(p.description) : true))
              .filter((p) => (p.usage || "UNDEFINED") === u)
              .sort((a, b) => a.name.localeCompare(b.name))

            if (!items.length) return null
            return (
              <Box key={u}>
                {idx !== 0 && <Divider sx={{ my: 1 }} />}
                <Typography variant="overline" sx={{ opacity: 0.7 }}>
                  {u} • {items.length}
                </Typography>
                <Stack sx={{ mt: 1 }} spacing={1}>
                  {items.map((p) => (
                    <Button
                      key={promptRowKey(p)}
                      variant="outlined"
                      size="small"
                      onClick={() => onClose(promptRowKey(p))}
                      sx={{
                        justifyContent: "flex-start",
                        textTransform: "none"
                      }}
                    >
                      {rowLabel(p)}
                    </Button>
                  ))}
                </Stack>
              </Box>
            )
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Kapat</Button>
      </DialogActions>
    </Dialog>
  )
}

export default function PromptDiff({
  open,
  onClose,
  prompts,
  defaultA = "",
  title = "Prompt Diff",
  maxWidth = "xl"
}: PromptDiffProps) {
  const { translate } = useTranslate()
  const [picking, setPicking] = useState<"A" | "B" | null>(null)
  const [pickA, setPickA] = useState(defaultA)
  const [pickB, setPickB] = useState("")
  const [q, setQ] = useState("")

  useEffect(() => {
    if (!open) return
    setPickA(defaultA)
    if (!pickB || pickB === defaultA) setPicking("B")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultA])

  const leftSel = useMemo(
    () => prompts.find((p) => promptRowKey(p) === pickA) ?? null,
    [prompts, pickA]
  )
  const rightSel = useMemo(
    () => prompts.find((p) => promptRowKey(p) === pickB) ?? null,
    [prompts, pickB]
  )

  const showDiff = open && !picking && !!leftSel && !!rightSel

  return (
    <>
      <PromptSelectDialog
        open={open && !!picking}
        title={translate(
          picking === "A" ? "prompt_compare_pick_a" : "prompt_compare_pick_b"
        )}
        prompts={prompts}
        onClose={(picked) => {
          const side = picking
          setPicking(null)
          if (picked && side === "A") setPickA(picked)
          if (picked && side === "B") setPickB(picked)
          if (!picked && (!leftSel || !rightSel)) onClose()
        }}
        q={q}
        setQ={setQ}
      />
      <Dialog open={showDiff} onClose={onClose} fullWidth maxWidth={maxWidth}>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ mr: 1 }}>
              {title}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPicking("A")}
              sx={{ textTransform: "none" }}
            >
              A: {leftSel ? rowLabel(leftSel) : "—"}
            </Button>
            <Tooltip title={translate("prompt_compare_swap")}>
              <IconButton
                size="small"
                onClick={() => {
                  setPickA(pickB)
                  setPickB(pickA)
                }}
              >
                <SwapHorizIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPicking("B")}
              sx={{ textTransform: "none" }}
            >
              B: {rightSel ? rowLabel(rightSel) : "—"}
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <PromptDiffPanel
            title="Prompt"
            oldValue={leftSel?.description ?? ""}
            newValue={rightSel?.description ?? ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
